// ApplicationForm.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The membership application form. Lives at the bottom of /apply-for-inclusion
// and replaces the placeholder section that was in the orchestrator.
//
// ADMIN WIRING (per spec, exact data flow):
//
//   On submit:
//     1. Insert into `listing_applications`
//          source_page='apply-for-inclusion'
//          (admin's ListingApplicationsModule sees it via existing query)
//     2. Insert into `leads`  (CRM)
//          lead_source='apply_for_inclusion'
//          lead_type='membership_application'
//          requirements_json carries: businessName, category, country,
//          region, website, instagram, message, investmentReady flag
//     3. Send confirmation email via send-email edge function
//          subject: "We've received your application, LWD"
//          wording: Apply for Inclusion (not List Your Business)
//     4. Best-effort write to application_email_log so admin's autoresponder
//          history shows the event. Falls through if the table doesn't exist.
//
//   Failure tolerance:
//     • Step 1 must succeed for the user to see the success state. Anything
//       else fails, the lead/email is logged but doesn't block.
//     • Step 2 (lead) failures: warn, continue.
//     • Step 3 (email) failures: warn, continue. Email is fire-and-forget.
//     • Step 4 (log) failures: warn, continue. Table is optional.
//
//   Status returned to the UI render path:
//     { application_saved, lead_created, autoresponder_sent }
//   So the success state can quietly indicate any partial degradation if
//   needed (currently the success page is uniform, failed lead/email do
//   not surface to the applicant).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient.js';
import { FORM, SUCCESS_CONTENT, PALETTE } from '../data/inclusionContent.js';

const GD   = 'var(--font-heading-primary)';
const NU   = 'var(--font-body)';
const GOLD = '#c9a96e';

// The form is intentionally ALWAYS DARK regardless of the page's lights
// toggle. It's the decision moment, like the Edit Showcase, a deliberate
// dark beat in the editorial rhythm. Dark forms read as confidential and
// serious; gold accents (eyebrow, investment box, CTA) read stronger
// against the dark surface.
const FORM_PALETTE = {
  ...PALETTE.dark,
  // Slightly deeper section bg + a richer card surface so the form has
  // visual weight without going pure black.
  altSurface: '#070604',                         // section bg
  surface:    '#111009',                         // card / input bg
  border:     'rgba(240,235,224,0.10)',
  border2:    'rgba(240,235,224,0.06)',
};

const CATEGORIES = [
  { key: 'venue',   label: 'Venue' },
  { key: 'planner', label: 'Planner' },
  { key: 'vendor',  label: 'Vendor' },
];

// ── Email template ──────────────────────────────────────────────────────────
function buildApplicationEmail({ name, businessName, category }) {
  const firstName = (name || '').trim().split(/\s+/)[0] || 'there';
  const catLabel  = ({ venue: 'Venue', planner: 'Planner', vendor: 'Vendor' })[category] || 'Member';
  const html = `
<!doctype html>
<html><head><meta charset="utf-8"><title>Application received, LWD</title></head>
<body style="margin:0;padding:0;background:#f7f3ec;font-family:'Nunito Sans',sans-serif;color:#1a1714;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f7f3ec;padding:48px 16px;">
    <tr><td align="center">
      <table role="presentation" width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;max-width:560px;width:100%;">
        <tr><td style="border-top:3px solid #c9a96e;padding:48px 40px 36px;">
          <p style="margin:0 0 6px;font-size:9px;letter-spacing:0.3em;text-transform:uppercase;color:#c9a96e;">
            Application Received
          </p>
          <h1 style="margin:0 0 24px;font-family:'Cormorant Garamond',Georgia,serif;font-size:30px;font-weight:400;color:#1a1714;letter-spacing:-0.01em;">
            Thank you, ${firstName}.
          </h1>
          <p style="margin:0 0 18px;font-size:15px;line-height:1.7;color:#3a3530;">
            We have received your application for <strong>${businessName || 'your business'}</strong>${catLabel ? ` (${catLabel})` : ''}. Our editorial team reviews every submission personally.
          </p>
          <p style="margin:0 0 18px;font-size:15px;line-height:1.7;color:#3a3530;">
            You will hear back within two weeks. Selected applicants are invited for a conversation about fit, positioning, and what membership would look like for your business.
          </p>
          <p style="margin:0 0 0;font-size:14px;line-height:1.7;color:#76706a;font-style:italic;">
            While you wait, you may enjoy this season’s issue of The LWD Edit:
            <a href="https://luxuryweddingdirectory.com/the-edit" style="color:#c9a96e;text-decoration:none;">luxuryweddingdirectory.com/the-edit</a>
          </p>
          <hr style="margin:32px 0;border:none;border-top:1px solid rgba(22,20,15,0.10);">
          <p style="margin:0;font-size:11px;letter-spacing:0.18em;text-transform:uppercase;color:#c9a96e;">
            Luxury Wedding Directory
          </p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`.trim();
  return {
    subject: "We've received your application, LWD",
    html,
  };
}

// ── Submission helpers ─────────────────────────────────────────────────────
async function saveApplication(form) {
  const { error } = await supabase.from('listing_applications').insert([{
    name:          form.name,
    email:         form.email,
    phone:         form.phone || null,
    business_name: form.businessName,
    category:      form.category,
    country:       form.country || null,
    region:        form.region || null,
    website:       form.website || null,
    instagram:     form.instagram || null,
    message:       form.message || null,
    source_page:   'apply-for-inclusion',
    status:        'new',
  }]);
  return { ok: !error, error };
}

async function createLead(form) {
  const parts = (form.name || '').trim().split(/\s+/);
  const { error } = await supabase.from('leads').insert([{
    first_name:  parts[0] || form.name,
    last_name:   parts.slice(1).join(' ') || '',
    email:       form.email,
    phone:       form.phone || null,
    lead_source: 'apply_for_inclusion',
    lead_channel: 'form',
    lead_type:   'membership_application',
    status:      'new',
    requirements_json: {
      businessName:    form.businessName,
      category:        form.category,
      country:         form.country,
      region:          form.region,
      website:         form.website,
      instagram:       form.instagram,
      message:         form.message,
      investmentReady: !!form.investmentReady,
      interests:       [form.category],
    },
  }]);
  return { ok: !error, error };
}

async function sendAutoresponder(form) {
  try {
    const tpl = buildApplicationEmail({
      name:         form.name,
      businessName: form.businessName,
      category:     form.category,
    });
    const { error } = await supabase.functions.invoke('send-email', {
      body: { ...tpl, to: form.email },
    });
    return { ok: !error, error };
  } catch (error) {
    return { ok: false, error };
  }
}

async function logEmailEvent({ form, autoresponderOk, error }) {
  // Best-effort log so admin autoresponder history shows the event.
  // Falls through silently if the table doesn't exist yet (set up later).
  try {
    await supabase.from('application_email_log').insert([{
      application_email:  form.email,
      application_source: 'apply-for-inclusion',
      template_key:       'application_received',
      subject:            "We've received your application, LWD",
      status:             autoresponderOk ? 'sent' : 'failed',
      error_message:      autoresponderOk ? null : (error?.message || 'unknown'),
    }]);
  } catch {
    /* table optional; never block on log */
  }
}

// ── ApplicationForm component ──────────────────────────────────────────────
// Note: darkMode prop accepted for API symmetry with other sections but is
// intentionally IGNORED, the form always uses FORM_PALETTE (dark). See the
// rationale at the top of this file.
export default function ApplicationForm({ formRef }) {
  const P = FORM_PALETTE;
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    businessName: '', category: 'venue',
    country: '', region: '',
    website: '', instagram: '',
    message: '',
    investmentReady: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState('');
  const [, setStatusFlags]          = useState({ application_saved: false, lead_created: false, autoresponder_sent: false });

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.businessName || !form.category) {
      setError('Please complete the required fields.');
      return;
    }
    // Phone is required, anti-spam filter. Real applicants don't mind.
    if (!form.phone || form.phone.replace(/\D/g, '').length < 6) {
      setError('Please add a phone number we can reach you on.');
      return;
    }
    if (!form.investmentReady) {
      setError('Please confirm your investment readiness to proceed.');
      return;
    }
    setError('');
    setSubmitting(true);

    // Step 1, application is the only blocking step.
    const appResult = await saveApplication(form);
    if (!appResult.ok) {
      setError('We could not submit your application. Please try again or email us directly.');
      console.error('[apply-for-inclusion] application save failed:', appResult.error);
      setSubmitting(false);
      return;
    }

    // Steps 2-4 are fire-and-forget. Failures warn but don't block the
    // success state, the applicant has done their part.
    const flags = { application_saved: true, lead_created: false, autoresponder_sent: false };

    const [leadResult, emailResult] = await Promise.all([
      createLead(form),
      sendAutoresponder(form),
    ]);
    flags.lead_created       = leadResult.ok;
    flags.autoresponder_sent = emailResult.ok;
    if (!leadResult.ok)  console.warn('[apply-for-inclusion] lead create failed:', leadResult.error);
    if (!emailResult.ok) console.warn('[apply-for-inclusion] autoresponder failed:', emailResult.error);

    // Best-effort autoresponder log (admin history surface).
    logEmailEvent({ form, autoresponderOk: emailResult.ok, error: emailResult.error });

    setStatusFlags(flags);
    setSubmitting(false);
    setSubmitted(true);

    // Bring the success message into view, accounting for fixed nav.
    window.requestAnimationFrame(() => {
      const el = formRef?.current;
      if (!el) return;
      const navEl = document.querySelector('.home-nav');
      const navHeight = navEl ? navEl.getBoundingClientRect().height : 100;
      const top = el.getBoundingClientRect().top + window.scrollY - navHeight - 24;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  }

  return (
    <section
      ref={formRef}
      id="application"
      style={{
        background: P.altSurface,
        padding:    'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
        borderTop:  `1px solid ${P.border}`,
        transition: 'background 0.3s ease, border-color 0.3s ease',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {submitted
          ? <SuccessState P={P} />
          : <FormView P={P} form={form} set={set} submitting={submitting} error={error} onSubmit={handleSubmit} />
        }
      </div>
    </section>
  );
}

// ── Form view ──────────────────────────────────────────────────────────────
function FormView({ P, form, set, submitting, error, onSubmit }) {
  const L = FORM.fieldLabels;
  return (
    <>
      {/* Eyebrow + title + pre-frame */}
      <div style={{ textAlign: 'center', marginBottom: 'clamp(32px, 4vw, 56px)' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
          <span style={{ width: 36, height: 1, background: GOLD }} />
          <span style={{ fontFamily: NU, fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD }}>
            Apply for Inclusion
          </span>
          <span style={{ width: 36, height: 1, background: GOLD }} />
        </div>

        {/* Pre-frame, the curated submission posture, sits above title */}
        {FORM.preFrame && (
          <p style={{
            fontFamily: GD,
            fontStyle:  'italic',
            fontSize:   'clamp(15px, 1.4vw, 18px)',
            fontWeight: 400,
            color:      P.body,
            lineHeight: 1.5,
            margin:     '0 auto 18px',
            maxWidth:   520,
            transition: 'color 0.3s ease',
          }}>
            {FORM.preFrame}
          </p>
        )}

        <h2 style={{ fontFamily: GD, fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 400, color: P.text, lineHeight: 1.12, letterSpacing: '-0.01em', margin: '0 0 12px', transition: 'color 0.3s ease' }}>
          {FORM.title}
        </h2>
        <p style={{ fontFamily: NU, fontSize: 'clamp(13px, 1.3vw, 15px)', color: P.muted, lineHeight: 1.7, margin: 0, transition: 'color 0.3s ease' }}>
          {FORM.description}
        </p>
      </div>

      <form onSubmit={onSubmit} noValidate>
        {/* Category toggle */}
        <Field label={L.category} required>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {CATEGORIES.map(c => {
              const active = form.category === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => set('category', c.key)}
                  style={{
                    padding: '10px 22px',
                    fontFamily: NU, fontSize: 10, fontWeight: 500,
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                    color:      active ? GOLD : P.muted,
                    background: 'none',
                    border:     `1px solid ${active ? GOLD : P.border}`,
                    cursor:     'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Property/Brand + Full Name */}
        <Row>
          <Field label={L.businessName} required>
            <Input P={P} value={form.businessName} onChange={v => set('businessName', v)} />
          </Field>
          <Field label={L.name} required>
            <Input P={P} value={form.name} onChange={v => set('name', v)} />
          </Field>
        </Row>

        {/* Email + Phone */}
        <Row>
          <Field label={L.email} required>
            <Input P={P} type="email" value={form.email} onChange={v => set('email', v)} />
          </Field>
          <Field label={L.phone} required>
            <Input P={P} type="tel" value={form.phone} onChange={v => set('phone', v)} placeholder="+44 …" />
          </Field>
        </Row>

        {/* Country + Region */}
        <Row>
          <Field label={L.country}>
            <Input P={P} value={form.country} onChange={v => set('country', v)} placeholder="e.g. Italy" />
          </Field>
          <Field label={L.region}>
            <Input P={P} value={form.region} onChange={v => set('region', v)} placeholder="e.g. Tuscany" />
          </Field>
        </Row>

        {/* Website + Instagram */}
        <Row>
          <Field label={L.website}>
            <Input P={P} value={form.website} onChange={v => set('website', v)} placeholder="https://" />
          </Field>
          <Field label={L.instagram}>
            <Input P={P} value={form.instagram} onChange={v => set('instagram', v)} placeholder="@handle" />
          </Field>
        </Row>

        {/* Brief */}
        <Field label={L.message}>
          <textarea
            value={form.message}
            onChange={e => set('message', e.target.value)}
            rows={4}
            placeholder={L.messagePlaceholder}
            style={{
              width: '100%',
              padding: '14px 16px',
              fontFamily: NU, fontSize: 14, lineHeight: 1.6,
              color: P.text,
              background: P.surface,
              border: `1px solid ${P.border}`,
              outline: 'none',
              resize: 'vertical',
              minHeight: 110,
              transition: 'border-color 0.2s, background 0.3s',
            }}
            onFocus={e => e.currentTarget.style.borderColor = GOLD}
            onBlur={e  => e.currentTarget.style.borderColor = P.border}
          />
        </Field>

        {/* Investment readiness, the friction filter */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 14,
          marginTop: 24, marginBottom: 28,
          padding: 'clamp(18px, 2vw, 24px)',
          background: P.surface,
          border: `1px solid ${GOLD}40`,
          cursor: 'pointer',
          transition: 'background 0.3s, border-color 0.2s',
        }}>
          <input
            type="checkbox"
            checked={form.investmentReady}
            onChange={e => set('investmentReady', e.target.checked)}
            style={{
              width: 16, height: 16, marginTop: 3,
              accentColor: GOLD, cursor: 'pointer', flexShrink: 0,
            }}
          />
          <div>
            <span style={{
              fontFamily: NU, fontSize: 13, lineHeight: 1.6,
              color: P.body,
              transition: 'color 0.3s ease',
              display: 'block',
              marginBottom: 4,
            }}>
              {FORM.investmentLabel}
            </span>
            {FORM.investmentSubtext && (
              <span style={{
                fontFamily: NU, fontSize: 11, lineHeight: 1.5,
                color: P.muted,
                transition: 'color 0.3s ease',
                display: 'block',
              }}>
                {FORM.investmentSubtext}
              </span>
            )}
          </div>
        </label>

        {/* Error */}
        {error && (
          <div style={{
            marginBottom: 20,
            padding: '12px 16px',
            background: 'rgba(184, 64, 48, 0.08)',
            border: '1px solid rgba(184, 64, 48, 0.25)',
            color: '#b84030',
            fontFamily: NU, fontSize: 13, lineHeight: 1.5,
          }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button
            type="submit"
            disabled={submitting}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 14,
              fontFamily: NU, fontSize: 11, fontWeight: 500,
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color:      submitting ? P.muted : '#0a0806',
              background: submitting ? P.surface : GOLD,
              border:     submitting ? `1px solid ${P.border}` : `1px solid ${GOLD}`,
              outline:    'none',
              cursor:     submitting ? 'wait' : 'pointer',
              padding:    '14px 36px',
              transition: 'opacity 0.2s, background 0.2s',
              opacity:    submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Submitting…' : FORM.cta}
            {!submitting && (
              <span aria-hidden style={{
                width: 5, height: 5, background: '#0a0806', transform: 'rotate(45deg)',
              }} />
            )}
          </button>
        </div>
      </form>
    </>
  );
}

// ── Success state ──────────────────────────────────────────────────────────
function SuccessState({ P }) {
  return (
    <div style={{ textAlign: 'center', padding: 'clamp(20px, 3vw, 40px) 0' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <span style={{ width: 36, height: 1, background: GOLD }} />
        <span aria-hidden style={{ width: 6, height: 6, background: GOLD, transform: 'rotate(45deg)' }} />
        <span style={{ width: 36, height: 1, background: GOLD }} />
      </div>

      <h2 style={{
        fontFamily: GD, fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 400,
        color: P.text, lineHeight: 1.08, letterSpacing: '-0.01em',
        margin: '0 0 16px',
        transition: 'color 0.3s ease',
      }}>
        {SUCCESS_CONTENT.headline}
      </h2>
      <p style={{
        fontFamily: NU, fontSize: 'clamp(14px, 1.4vw, 17px)',
        color: P.body, lineHeight: 1.7,
        margin: '0 auto', maxWidth: 480,
        transition: 'color 0.3s ease',
      }}>
        {SUCCESS_CONTENT.subline}
      </p>

      {/* What happens next */}
      <div style={{
        marginTop: 'clamp(40px, 5vw, 64px)',
        padding: 'clamp(28px, 3vw, 40px)',
        background: P.surface,
        borderTop: `1px solid ${GOLD}40`,
        textAlign: 'left',
        maxWidth: 520, marginLeft: 'auto', marginRight: 'auto',
        transition: 'background 0.3s ease',
      }}>
        <div style={{
          fontFamily: NU, fontSize: 9, fontWeight: 600,
          letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD, marginBottom: 18,
        }}>
          {SUCCESS_CONTENT.stepsTitle}
        </div>
        <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {SUCCESS_CONTENT.steps.map((step, i) => (
            <li key={i} style={{
              display: 'flex', gap: 16, alignItems: 'flex-start',
              padding: '12px 0',
              borderTop: i === 0 ? 'none' : `1px solid ${P.border}`,
            }}>
              <span style={{
                fontFamily: GD, fontSize: 14, fontWeight: 400,
                color: GOLD, lineHeight: 1.5, flexShrink: 0,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span style={{
                fontFamily: NU, fontSize: 14, lineHeight: 1.6,
                color: P.text,
                transition: 'color 0.3s ease',
              }}>
                {step}
              </span>
            </li>
          ))}
        </ol>
      </div>

      {/* Editorial gift, keeps applicants warm during the wait */}
      <a
        href={SUCCESS_CONTENT.ctaHref}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 12,
          marginTop: 'clamp(36px, 4vw, 56px)',
          fontFamily: NU, fontSize: 11, fontWeight: 500,
          letterSpacing: '0.28em', textTransform: 'uppercase',
          color: GOLD, textDecoration: 'none',
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        {SUCCESS_CONTENT.ctaLabel}
        <span aria-hidden style={{
          width: 5, height: 5, background: GOLD, transform: 'rotate(45deg)',
        }} />
      </a>
    </div>
  );
}

// ── Form primitives ────────────────────────────────────────────────────────
function Row({ children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 'clamp(16px, 2vw, 24px)' }}>
      {children}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block',
        fontFamily: NU, fontSize: 9, fontWeight: 600,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: GOLD, marginBottom: 8,
      }}>
        {label}{required && <span style={{ marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function Input({ P, type = 'text', value, onChange, placeholder }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '12px 16px',
        fontFamily: NU, fontSize: 14,
        color: P.text,
        background: P.surface,
        border: `1px solid ${P.border}`,
        outline: 'none',
        transition: 'border-color 0.2s, background 0.3s',
      }}
      onFocus={e => e.currentTarget.style.borderColor = GOLD}
      onBlur={e  => e.currentTarget.style.borderColor = P.border}
    />
  );
}
